import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMyProponentProfile, upsertMyProponentProfile } from '../services/proponentService';

const ORG_TYPES = [
  { value: 'company',    label: 'Company / Corporation' },
  { value: 'firm',       label: 'Firm (Engineering / Legal / etc.)' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'ngo',        label: 'Non-Governmental Organisation' },
  { value: 'individual', label: 'Individual / Sole Proprietor' },
  { value: 'government', label: 'Government Agency' },
  { value: 'other',      label: 'Other' },
];

const inp = {
  background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
  color: '#e2e8f0', padding: '8px 11px', fontSize: 13,
  width: '100%', boxSizing: 'border-box',
};
const lbl = { color: '#94a3b8', fontSize: 11, marginBottom: 4, display: 'block' };
const section = {
  background: '#1e293b', borderRadius: 10, padding: 22, marginBottom: 20,
};
const sectionTitle = { fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 16 };
const row = { display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' };
const field1 = { flex: '1 1 200px' };

const EMPTY = {
  orgName: '',
  orgType: 'company',
  registrationNumber: '',
  address: '',
  city: '',
  province: '',
  country: 'Canada',
  postalCode: '',
  website: '',
  primaryContact: { name: '', title: '', phone: '', email: '' },
};

// ─── ProponentProfilePage ─────────────────────────────────────────────────────
export default function ProponentProfilePage() {
  const [form, setForm]       = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');
  const [isNew, setIsNew]     = useState(false);

  useEffect(() => {
    getMyProponentProfile()
      .then(r => {
        const p = r.data;
        setForm({
          orgName:            p.orgName            || '',
          orgType:            p.orgType            || 'company',
          registrationNumber: p.registrationNumber || '',
          address:            p.address            || '',
          city:               p.city               || '',
          province:           p.province           || '',
          country:            p.country            || 'Canada',
          postalCode:         p.postalCode         || '',
          website:            p.website            || '',
          primaryContact: {
            name:  p.primaryContact?.name  || '',
            title: p.primaryContact?.title || '',
            phone: p.primaryContact?.phone || '',
            email: p.primaryContact?.email || '',
          },
        });
      })
      .catch(err => {
        if (err.response?.status === 404) {
          setIsNew(true);
          setForm(EMPTY);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setContact = (k, v) => setForm(p => ({
    ...p,
    primaryContact: { ...p.primaryContact, [k]: v },
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    if (!form.orgName.trim()) { setMsg('Organisation name is required.'); return; }
    setSaving(true);
    try {
      await upsertMyProponentProfile(form);
      setMsg('Profile saved successfully.');
      setIsNew(false);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ padding: '40px 32px', color: '#94a3b8', fontSize: 13 }}>Loading profile…</div>
  );

  return (
    <div style={{
      padding: '28px 32px', maxWidth: 760, margin: '0 auto',
      color: '#e2e8f0', minHeight: '100vh', boxSizing: 'border-box',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 26 }}>
        <Link
          to="/proponent"
          style={{
            background: 'none', border: 'none', color: '#7dd3fc',
            textDecoration: 'none', fontSize: 20, lineHeight: 1,
          }}
          title="Back to My Projects"
        >&#8592;</Link>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9' }}>Proponent Profile</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            {isNew
              ? 'Complete your organisation profile. This information may appear on your project submissions.'
              : 'Update your organisation and contact information.'}
          </div>
        </div>
      </div>

      {msg && (
        <div style={{
          padding: '10px 14px', borderRadius: 7, marginBottom: 16, fontSize: 13,
          background: msg.includes('successfully') ? '#14532d' : '#450a0a',
          color:      msg.includes('successfully') ? '#86efac' : '#fca5a5',
          border: `1px solid ${msg.includes('successfully') ? '#22c55e' : '#ef4444'}`,
        }}>
          {msg}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* ── Organisation Info ── */}
        <div style={section}>
          <div style={sectionTitle}>Organisation Information</div>

          <div style={row}>
            <div style={{ flex: '2 1 260px' }}>
              <span style={lbl}>ORGANISATION NAME *</span>
              <input
                style={inp}
                value={form.orgName}
                onChange={e => set('orgName', e.target.value)}
                placeholder="e.g. Northlands Energy Corp."
                required
              />
            </div>
            <div style={{ flex: '1 1 180px' }}>
              <span style={lbl}>ORGANISATION TYPE</span>
              <select style={inp} value={form.orgType} onChange={e => set('orgType', e.target.value)}>
                {ORG_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          <div style={row}>
            <div style={field1}>
              <span style={lbl}>REGISTRATION / BUSINESS NUMBER</span>
              <input
                style={inp}
                value={form.registrationNumber}
                onChange={e => set('registrationNumber', e.target.value)}
                placeholder="e.g. 123456789 RC0001"
              />
            </div>
            <div style={field1}>
              <span style={lbl}>WEBSITE</span>
              <input
                style={inp}
                type="url"
                value={form.website}
                onChange={e => set('website', e.target.value)}
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <span style={lbl}>STREET ADDRESS</span>
            <input
              style={inp}
              value={form.address}
              onChange={e => set('address', e.target.value)}
              placeholder="Street address"
            />
          </div>

          <div style={row}>
            <div style={field1}>
              <span style={lbl}>CITY</span>
              <input style={inp} value={form.city} onChange={e => set('city', e.target.value)} placeholder="City" />
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <span style={lbl}>PROVINCE / STATE</span>
              <input style={inp} value={form.province} onChange={e => set('province', e.target.value)} placeholder="Province" />
            </div>
            <div style={{ flex: '1 1 100px' }}>
              <span style={lbl}>POSTAL CODE</span>
              <input style={inp} value={form.postalCode} onChange={e => set('postalCode', e.target.value)} placeholder="T1A 2B3" />
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <span style={lbl}>COUNTRY</span>
              <input style={inp} value={form.country} onChange={e => set('country', e.target.value)} placeholder="Canada" />
            </div>
          </div>
        </div>

        {/* ── Primary Contact ── */}
        <div style={section}>
          <div style={sectionTitle}>Primary Contact</div>
          <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16, marginTop: 0 }}>
            The main contact for project enquiries. Projects can override this on a per-project basis.
          </p>

          <div style={row}>
            <div style={field1}>
              <span style={lbl}>FULL NAME</span>
              <input
                style={inp}
                value={form.primaryContact.name}
                onChange={e => setContact('name', e.target.value)}
                placeholder="Jane Smith"
              />
            </div>
            <div style={field1}>
              <span style={lbl}>TITLE / POSITION</span>
              <input
                style={inp}
                value={form.primaryContact.title}
                onChange={e => setContact('title', e.target.value)}
                placeholder="Environmental Consultant"
              />
            </div>
          </div>

          <div style={row}>
            <div style={field1}>
              <span style={lbl}>PHONE</span>
              <input
                style={inp}
                type="tel"
                value={form.primaryContact.phone}
                onChange={e => setContact('phone', e.target.value)}
                placeholder="+1 (403) 555-0123"
              />
            </div>
            <div style={field1}>
              <span style={lbl}>EMAIL</span>
              <input
                style={inp}
                type="email"
                value={form.primaryContact.email}
                onChange={e => setContact('email', e.target.value)}
                placeholder="jane@example.com"
              />
            </div>
          </div>
        </div>

        {/* ── Actions ── */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              background: '#1d4ed8', border: 'none', borderRadius: 7,
              color: '#fff', padding: '9px 24px', cursor: 'pointer',
              fontSize: 13, fontWeight: 700, opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : isNew ? 'Create Profile' : 'Save Changes'}
          </button>
          <Link to="/proponent" style={{ fontSize: 12, color: '#64748b' }}>
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
